import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropzone", "fileName", "fileSize", "uploadedAt", "uploadSection", "existingSection", "error", "loading"]
  static values = {
    uploadUrl: String,
    deleteUrl: String
  }

  connect() {
    this.setupDragAndDrop()
  }

  setupDragAndDrop() {
    const dropzone = this.dropzoneTarget

    dropzone.addEventListener("dragover", (e) => {
      e.preventDefault()
      dropzone.classList.add("border-indigo-500", "bg-indigo-50")
    })

    dropzone.addEventListener("dragleave", (e) => {
      e.preventDefault()
      dropzone.classList.remove("border-indigo-500", "bg-indigo-50")
    })

    dropzone.addEventListener("drop", (e) => {
      e.preventDefault()
      dropzone.classList.remove("border-indigo-500", "bg-indigo-50")

      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.uploadFile(files[0])
      }
    })
  }

  selectFile() {
    this.inputTarget.click()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      this.uploadFile(file)
    }
  }

  async uploadFile(file) {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ]

    if (!allowedTypes.includes(file.type)) {
      this.showError("Invalid file type. Only PDF and DOC/DOCX files are allowed.")
      return
    }

    if (file.size > 50 * 1024 * 1024) {
      this.showError("File too large. Maximum size is 50MB.")
      return
    }

    this.hideError()
    this.showLoading()

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showExistingFile(data.agreement)
      } else {
        this.showError(data.error || "Upload failed. Please try again.")
      }
    } catch (error) {
      this.showError("Upload failed. Please try again.")
    } finally {
      this.hideLoading()
      this.inputTarget.value = ""
    }
  }

  async deleteFile(event) {
    event.preventDefault()

    if (!confirm("Are you sure you want to delete this agreement?")) {
      return
    }

    this.showLoading()

    try {
      const response = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showUploadSection()
      } else {
        this.showError(data.error || "Delete failed. Please try again.")
      }
    } catch (error) {
      this.showError("Delete failed. Please try again.")
    } finally {
      this.hideLoading()
    }
  }

  showExistingFile(agreement) {
    this.fileNameTarget.textContent = agreement.file_name
    this.fileSizeTarget.textContent = agreement.file_size
    this.uploadedAtTarget.textContent = agreement.uploaded_at
    this.uploadSectionTarget.classList.add("hidden")
    this.existingSectionTarget.classList.remove("hidden")
  }

  showUploadSection() {
    this.uploadSectionTarget.classList.remove("hidden")
    this.existingSectionTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.classList.add("hidden")
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
  }

  hideLoading() {
    this.loadingTarget.classList.add("hidden")
  }
}
